import React, { useState, useEffect, useRef } from 'react';
import { fetchInternalLinkData, InternalLinkData } from '../services/openGraphService';
import { useNavigate } from 'react-router-dom';

// Интерфейс компонента предпросмотра внутренних ссылок
interface InternalLinkPreviewProps {
  url: string;
  position: { x: number; y: number };
  onClose: () => void;
  onRemoveLink?: () => void;
}

/**
 * Компонент предпросмотра внутренних ссылок
 * Отображает информацию о внутреннем документе в базе знаний
 */
const InternalLinkPreview = ({ url, position, onClose, onRemoveLink }: InternalLinkPreviewProps) => {
  const [previewData, setPreviewData] = useState<InternalLinkData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const previewRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Корректировка позиции, чтобы превью не выходило за границы экрана
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Проверка и коррекция позиции превью
  useEffect(() => {
    const adjustPosition = () => {
      // Не выполняем корректировку, если еще нет ссылки на DOM-элемент
      if (!previewRef.current) return;
      
      const previewRect = previewRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let newX = position.x;
      let newY = position.y;
      
      // Проверяем, не выходит ли превью за правый край
      if (position.x + previewRect.width/2 > viewportWidth) {
        newX = viewportWidth - previewRect.width/2 - 10;
      }
      
      // Проверяем, не выходит ли превью за левый край
      if (position.x - previewRect.width/2 < 0) {
        newX = previewRect.width/2 + 10;
      }
      
      // Проверяем, не выходит ли превью за нижний край
      if (position.y + previewRect.height > viewportHeight + window.scrollY) {
        // Если выходит за нижний край, размещаем над ссылкой
        newY = position.y - previewRect.height - 20; 
      }
      
      // Обновляем позицию только если она изменилась
      if (newX !== adjustedPosition.x || newY !== adjustedPosition.y) {
        setAdjustedPosition({ x: newX, y: newY });
      }
    };
    
    // Первичная коррекция позиции
    setTimeout(adjustPosition, 50);
    
    // Корректируем позицию при изменении размера окна
    window.addEventListener('resize', adjustPosition);
    return () => window.removeEventListener('resize', adjustPosition);
  }, [position, adjustedPosition]);

  // Монтирование/размонтирование компонента
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Получение данных для внутренней ссылки
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Получение ID из URL
        const pathParts = url.split('/');
        const itemId = pathParts[pathParts.length - 1];
        
        const data = await fetchInternalLinkData(url);
        
        // Проверяем, что компонент все еще смонтирован
        if (isMounted.current) {
          setPreviewData(data);
          setError(null);
        }
      } catch (err) {
        if (isMounted.current) {
          setError('Не удалось загрузить данные');
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [url]);

  // Функция для перехода к документу без перезагрузки страницы
  const handleNavigation = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!previewData || !previewData.id) {
      return;
    }
    let path = `/knowledge/${previewData.id}`;

    
    console.log(`[InternalLinkPreview] Переход по пути: ${path}`);
    
    // Закрываем превью
    onClose();
    
    // Переходим по указанному пути
    navigate(path);
  };

  // Функция для локального удаления ссылки
  const handleLocalRemoveLink = (e: React.MouseEvent) => {
    e.preventDefault();  // Предотвращаем переход по умолчанию
    e.stopPropagation(); // Предотвращаем всплытие события
    
    console.log('[InternalLinkPreview] Вызов удаления локальной ссылки');
    
    // Вызываем переданную функцию удаления ссылки
    if (onRemoveLink) {
      onRemoveLink();
    } else {
      console.error('[InternalLinkPreview] Функция onRemoveLink не определена');
    }
    
    // Закрываем превью
    onClose();
  };

  // Возвращает иконку для типа документа
  const getTypeIcon = () => {
    if (!previewData || !previewData.type) return null;
    
    // Выбираем иконку в зависимости от типа документа
    switch(previewData.type) {
      case 'article':
      case 'summary':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        );
      case 'folder':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        );
      case 'notes':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'educational':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M12 14l9-5-9-5-9 5 9 5z" />
            <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
          </svg>
        );
      case 'roadmap':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
    }
  };
  
  // Получаем локализованное название типа документа
  const getTypeName = () => {
    if (!previewData || !previewData.type) return 'Документ';
    
    switch(previewData.type) {
      case 'article': return 'Статья';
      case 'summary': return 'Конспект';
      case 'folder': return 'Папка';
      case 'notes': return 'Заметки';
      case 'educational': return 'Учебный материал';
      case 'roadmap': return 'Дорожная карта';
      case 'reels': return 'Видеоролик';
      default: return 'Документ';
    }
  };

  return (
    <div 
      className="internal-link-preview-container"
      style={{
        position: 'fixed',
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        zIndex: 9999,
        transform: 'translateX(-50%)',
        pointerEvents: 'all',
      }}
      ref={previewRef}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-72">
        {isLoading ? (
          <div className="p-4 flex items-center justify-center h-32">
            <div className="animate-pulse">Загрузка предпросмотра...</div>
          </div>
        ) : error ? (
          <div className="p-4 flex items-center justify-center h-32">
            <div className="text-red-500">{error}</div>
          </div>
        ) : (
          <div className="p-4">
            {/* Заголовок с иконкой */}
            <div className="flex items-center mb-2">
              <div className="text-blue-500 dark:text-blue-400 mr-2">
                {getTypeIcon()}
              </div>
              <h3 className="text-md font-semibold text-gray-800 dark:text-gray-200 line-clamp-2">
                {previewData?.title || 'Без заголовка'}
              </h3>
            </div>
            
            {/* Тип документа */}
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {getTypeName()}
              {previewData?.type === 'folder' && typeof previewData.childCount !== 'undefined' && (
                <span className="ml-1 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">
                  {previewData.childCount} эл.
                </span>
              )}
              {/* ID не отображается */}
            </div>
            
            {/* Описание/контент документа */}
            {(previewData?.description && ['article', 'notes', 'educational'].includes(previewData.type || '')) && (
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3 mb-2">
                {previewData.description}
              </p>
            )}
            
            {/* Дата создания/обновления */}
            {previewData?.createdAt && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Создано: {new Date(previewData.createdAt).toLocaleDateString('ru-RU')}
                {previewData.updatedAt && previewData.updatedAt !== previewData.createdAt && (
                  <div>Обновлено: {new Date(previewData.updatedAt).toLocaleDateString('ru-RU')}</div>
                )}
              </div>
            )}
            
            {/* Кнопки действий */}
            <div className="flex justify-between mt-2">
              {onRemoveLink && (
                <button 
                  className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 
                            text-gray-600 dark:text-gray-300 text-xs py-1 px-2 rounded-md transition-colors"
                  onClick={handleLocalRemoveLink}
                >
                  Удалить ссылку
                </button>
              )}
              <button 
                className="bg-blue-500 hover:bg-blue-600 text-white text-xs py-1 px-3 rounded-md transition-colors ml-auto"
                onClick={handleNavigation}
              >
                Перейти
              </button>
            </div>
          </div>
        )}
        
        {/* Кнопка закрытия */}
        <button 
          className="absolute top-2 right-2 bg-white dark:bg-gray-800 rounded-full p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
        style={{ pointerEvents: 'none' }}
      />
    </div>
  );
};

export default InternalLinkPreview; 